import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { sessionQueryKey, useSession } from '@/features/auth/api/use-session';
import { LOGIN_PATH } from '@/features/auth/constants';
import { onUnauthenticated } from '@/lib/api-client';
import { readQuestionnaireCustomerModeMarker } from '@/lib/questionnaire-customer-mode';

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { data, isPending, isError } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const customerMode = readQuestionnaireCustomerModeMarker();

  useEffect(
    () =>
      onUnauthenticated(() => {
        queryClient.setQueryData(sessionQueryKey, null);
        navigate(LOGIN_PATH, {
          replace: true,
          state: { from: `${location.pathname}${location.search}${location.hash}` },
        });
      }),
    [navigate, queryClient, location.pathname, location.search, location.hash],
  );

  if (customerMode !== null && location.pathname !== customerMode.customerPath) {
    return <Navigate to={customerMode.customerPath} replace />;
  }

  if (isPending) {
    return (
      <div className="flex min-h-full flex-col gap-4 p-6" aria-busy="true">
        <span className="sr-only">{t('auth.checking')}</span>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || data === null || data === undefined) {
    return (
      <Navigate
        to={LOGIN_PATH}
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  return <>{children}</>;
}
